import { useTenantStore } from '@/store/tenantStore';
import { getLogoImageUrl } from '@/services/tenant/tenantImages';
import { useLocalization } from '@/hooks/useLocalization';
import { AppFooter } from '@/components/layout/AppFooter';
import styles from './MaintenancePage.module.css';

/**
 * MaintenancePage — mirrors Flutter presentation/common/maintenance_page.dart.
 * Shown in place of every route while content.isMaintainancePageVisible is set (AppRouter).
 *
 * Logo, then a card with the tools icon, "We're Under Maintenance", the tenant's
 * maintenanceTimeText (Constants.maintenanceTimeText) and a thank-you line; below it the support
 * address (Constants.supportEmailId = contentSettings.deleteAccountContent.emailId) as a mailto
 * link (GenericHelpers.sendEmail), then the footer. Flutter's pull-to-refresh just reloads the
 * page, which the mobile browser already does natively.
 */
export function MaintenancePage() {
  const { t } = useLocalization();
  const { content } = useTenantStore();
  const brandName = content?.contentSettings?.general?.name ?? 'Career Portal';
  const logoUrl = getLogoImageUrl(content);
  const maintenanceTimeText = content?.maintenanceTimeText ?? '';
  const supportEmailId = content?.contentSettings?.deleteAccountContent?.emailId ?? '';

  return (
    <div className={styles.page}>
      <div className={styles.body}>
        {logoUrl && (
          <div className={styles.logoRow}>
            <img src={logoUrl} alt={brandName} className={styles.logo} />
          </div>
        )}

        <div className={styles.center}>
          <div className={styles.card}>
            <ToolsIcon />
            <h1 className={styles.title}>{t("We're Under Maintenance")}</h1>
            <p className={styles.text}>
              {t('Our website is currently down for scheduled maintenance from:')}
            </p>
            <p className={styles.time}>{maintenanceTimeText}</p>
            <p className={`${styles.text} ${styles.thanks}`}>{t('Thank you for your patience!')}</p>
          </div>

          <p className={styles.contact}>{t('For any urgent inquiries, please contact us at')}</p>
          <a href={`mailto:${supportEmailId}`} className={styles.email}>{supportEmailId}</a>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

/** Flutter FaIcon(FontAwesomeIcons.tools) */
function ToolsIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.2L3.6 17.2a1.6 1.6 0 0 0 2.3 2.3l5.7-5.7a4 4 0 0 0 5.2-5.4l-2.4 2.4-2.2-.6-.6-2.2 2.4-2.4Z" />
      <path d="m14 15 4.6 4.6a1.6 1.6 0 0 0 2.3-2.3L16.3 12.7" />
      <path d="M4 4.5 5.5 3l3 2v1.5l2.2 2.2-1.5 1.5L7 8H5.5l-1.5-3Z" />
    </svg>
  );
}
